import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, PieChart, Pie, Cell, Legend } from "recharts";
import {
  Users,
  UserPlus,
  AlertCircle,
  TrendingUp,
  Mail,
  MailCheck,
  ArrowRight,
  Download,
  DatabaseBackup,
  Flame,
  CalendarClock,
  Handshake,
  Kanban,
} from "lucide-react";
import { getDashboard } from "@/lib/admin-api";
import adminApi from "@/lib/admin-api";
import { toast } from "sonner";
import { formatMtyDateShort, formatMtyTime, MONTERREY_TZ } from "@/lib/mty-date";
import { STATUS_LABELS, STATUS_BADGE, SOURCE_LABELS, tempBadgeClass, tempLabel } from "@/lib/lead-constants";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    getDashboard()
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleRestore = async (mode = "merge") => {
    const msg = mode === "wipe"
      ? "⚠️ SINCRONIZAR exacto desde el respaldo:\n\nEsto BORRA todos los leads, suscriptores, campañas y clientes actuales y los reemplaza con los del respaldo.\n\nÚsalo solo si quieres que producción quede IDÉNTICA al preview.\n\n¿Continuar?"
      : "¿Importar datos de respaldo? (Leads + Suscriptores + Campañas)\n\nEsto AGREGA los datos que falten sin borrar los existentes.";
    if (!window.confirm(msg)) return;
    setRestoring(true);
    try {
      const { data } = await adminApi.post(`/admin/seed/restore-backup?mode=${mode}`);
      const s = data.stats || {};
      const summary = Object.entries(s).map(([k, v]) =>
        mode === "wipe"
          ? `${k}: ${v.inserted || 0}`
          : `${k}: +${v.inserted || 0} nuevos${v.updated ? ', ' + v.updated + ' actualizados' : ''}`
      ).join(" · ");
      toast.success(`${mode === "wipe" ? "Sincronización" : "Importación"} completa. ${summary}`);
      const fresh = await getDashboard();
      setData(fresh);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al restaurar datos");
    } finally {
      setRestoring(false);
    }
  };

  if (loading) return <div className="p-8 text-neutral-500">Cargando...</div>;
  if (!data) return <div className="p-8 text-red-600">Error al cargar dashboard.</div>;

  const tipoBarData = Object.entries(data.tipo_counts).map(([k, v]) => ({ name: k, value: v }));
  const fechaMin = data.daily_series?.[0]?.date?.slice(5) || "";
  const fechaMax = data.daily_series?.slice(-1)[0]?.date?.slice(5) || "";

  return (
    <div className="p-5 md:p-8 max-w-7xl">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h1 className="section-title text-3xl md:text-4xl font-black">Dashboard</h1>
          <p className="text-neutral-500 text-sm mt-1">Resumen general de leads FlexShade · <span className="text-neutral-400">Horario: Monterrey (CST, UTC-6)</span></p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {data.total === 0 ? (
            <button
              onClick={() => handleRestore("merge")}
              disabled={restoring}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-60 shadow-sm"
              data-testid="restore-backup-btn"
              title="Importar los datos de respaldo (Leads + Suscriptores + Campañas)"
            >
              <DatabaseBackup className="w-4 h-4" />
              {restoring ? "Restaurando..." : "Importar datos de respaldo"}
            </button>
          ) : (
            <button
              onClick={() => handleRestore("wipe")}
              disabled={restoring}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-red-300 text-red-700 text-xs font-semibold hover:bg-red-50 disabled:opacity-60"
              data-testid="resync-backup-btn"
              title="Borra todos los datos y los reemplaza con el respaldo bundled"
            >
              <DatabaseBackup className="w-3.5 h-3.5" />
              {restoring ? "Sincronizando..." : "Re-sincronizar desde respaldo"}
            </button>
          )}
          {data.emails_enabled ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-50 border border-green-200 text-green-700 text-xs font-semibold">
              <MailCheck className="w-3.5 h-3.5" /> Emails activos
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-xs font-semibold" title="Agrega RESEND_API_KEY en el panel de deploy">
              <Mail className="w-3.5 h-3.5" /> Emails pendientes
            </span>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard icon={Users} color="red" label="Total leads" value={data.total} />
        <KpiCard icon={UserPlus} color="orange" label="Hoy" value={data.today} />
        <KpiCard icon={AlertCircle} color="yellow" label="Por contactar" value={data.to_contact} />
        <KpiCard icon={TrendingUp} color="green" label="Tasa de respuesta" value={`${data.response_rate}%`} />
      </div>

      {/* Widget HOY */}
      {data.hoy_widget && <HoyWidget hoy={data.hoy_widget} />}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-neutral-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-base">Leads por día</h3>
              <p className="text-xs text-neutral-500">{fechaMin} → {fechaMax} (14 días)</p>
            </div>
          </div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={data.daily_series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tickFormatter={(d) => d?.slice(5)} fontSize={11} stroke="#888" />
                <YAxis allowDecimals={false} fontSize={11} stroke="#888" />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#DC2626" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-neutral-200 p-5">
          <h3 className="font-bold text-base mb-2">Por tipo de espacio</h3>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={tipoBarData} layout="vertical" margin={{ left: 0, right: 10 }}>
                <XAxis type="number" allowDecimals={false} fontSize={11} stroke="#888" />
                <YAxis type="category" dataKey="name" width={90} fontSize={11} stroke="#888" />
                <Tooltip />
                <Bar dataKey="value" fill="#F97316" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Status & Source rows */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-neutral-200 p-5">
          <h3 className="font-bold text-base mb-4">Por estatus</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(data.status_counts).map(([k, v]) => (
              <span key={k} className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${STATUS_BADGE[k] || ""}`}>
                {STATUS_LABELS[k] || k}: <strong>{v}</strong>
              </span>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-neutral-200 p-5">
          <h3 className="font-bold text-base mb-4">Por fuente</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(data.source_counts).map(([k, v]) => (
              <span key={k} className="text-xs font-semibold px-3 py-1.5 rounded-full border bg-neutral-50 border-neutral-200 text-neutral-700">
                {SOURCE_LABELS[k] || k}: <strong>{v}</strong>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Channel distribution (Analytics Nivel 1) */}
      {data.channel_distribution && <ChannelDistribution dist={data.channel_distribution} />}

      {/* Last leads */}
      <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-neutral-100">
          <h3 className="font-bold text-base">Últimos leads</h3>
          <Link to="/admin/leads" className="text-red-600 text-sm font-semibold inline-flex items-center gap-1 hover:text-red-700">
            Ver todos <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="divide-y divide-neutral-100">
          {data.last_leads.length === 0 ? (
            <div className="p-8 text-center text-neutral-400 text-sm">No hay leads todavía.</div>
          ) : (
            data.last_leads.map((l) => (
              <Link key={l.id} to={`/admin/leads/${l.id}`} className="flex items-center justify-between gap-3 p-4 hover:bg-neutral-50 transition">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-neutral-900 truncate">{l.nombre}</p>
                  <p className="text-xs text-neutral-500 truncate">
                    {l.telefono} {l.email ? `· ${l.email}` : ""} · {l.tipo_espacio || "-"} · {l.ciudad || "-"}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-[10px] font-semibold px-2 py-1 rounded-full border ${STATUS_BADGE[l.estatus] || ""}`}>
                    {STATUS_LABELS[l.estatus] || l.estatus}
                  </span>
                  <span className="text-xs text-neutral-400">
                    {new Date(l.created_at).toLocaleDateString("es-MX", { day: "2-digit", month: "short" })}
                  </span>
                </div>
              </Link>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, color, label, value }) {
  const colors = {
    red: "from-red-500 to-red-600",
    orange: "from-orange-500 to-orange-600",
    yellow: "from-yellow-500 to-yellow-600",
    green: "from-green-500 to-green-600",
  };
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-4 md:p-5">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colors[color]} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-neutral-500 font-medium">{label}</p>
          <p className="text-2xl font-black tracking-tight section-title">{value}</p>
        </div>
      </div>
    </div>
  );
}


// ============================
//  Widget "Hoy"
// ============================
function HoyWidget({ hoy }) {
  const items = [
    { k: "follow_ups_hoy", label: "Seguimientos para hoy", value: hoy.follow_ups_hoy, icon: CalendarClock, color: "from-blue-500 to-blue-600", accent: "text-blue-600", tooltip: "Leads con next_followup_at dentro de 24h" },
    { k: "overdue", label: "Seguimientos vencidos", value: hoy.overdue, icon: AlertCircle, color: "from-red-500 to-red-600", accent: "text-red-600", tooltip: "Seguimientos con fecha anterior a hoy" },
    { k: "hot_no_contactados", label: "Leads HOT sin contactar", value: hoy.hot_no_contactados, icon: Flame, color: "from-orange-500 to-red-500", accent: "text-orange-600", tooltip: "Leads con score alto que aún están en 'nuevo'" },
    { k: "cerrando", label: "En cierre (visita/negociación)", value: hoy.cerrando, icon: Handshake, color: "from-emerald-500 to-green-600", accent: "text-emerald-600", tooltip: "Visita agendada, negociación o anticipo" },
  ];
  return (
    <div className="bg-gradient-to-br from-white to-neutral-50 border border-neutral-200 rounded-2xl p-5 mb-6" data-testid="hoy-widget">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-black text-lg flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-red-600" /> Hoy
          </h2>
          <p className="text-xs text-neutral-500">Qué atender ahora para no perder ventas</p>
        </div>
        <Link to="/admin/pipeline" className="inline-flex items-center gap-1 text-sm font-semibold text-red-600 hover:text-red-700">
          <Kanban className="w-4 h-4" /> Ir al Pipeline <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <div key={it.k} className="bg-white border border-neutral-200 rounded-xl p-4" title={it.tooltip} data-testid={`hoy-kpi-${it.k}`}>
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${it.color} flex items-center justify-center`}>
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <p className="text-[11px] text-neutral-500 leading-tight">{it.label}</p>
              </div>
              <p className={`text-2xl font-black tracking-tight ${it.accent}`}>{it.value ?? 0}</p>
            </div>
          );
        })}
      </div>

      {(hoy.hot_leads_top || []).length > 0 && (
        <div>
          <h4 className="text-xs font-bold text-neutral-600 uppercase tracking-wider mb-2">
            Top leads por score (pendientes de contacto)
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {hoy.hot_leads_top.slice(0, 4).map((l) => (
              <Link
                key={l.id}
                to={`/admin/leads/${l.id}`}
                className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-white border border-neutral-200 hover:border-red-300 hover:shadow-sm transition"
                data-testid={`hoy-hot-lead-${l.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm text-neutral-900 truncate">{l.nombre}</p>
                  <p className="text-[11px] text-neutral-500 truncate">
                    {l.empresa || (l.tipo_espacio ? `${l.tipo_espacio}` : "—")} · {l.responsable || "sin asignar"}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${tempBadgeClass(l.lead_temp || "cold")}`}>
                    {tempLabel(l.lead_temp || "cold")}
                  </span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-700">
                    {l.lead_score ?? 0}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================
//  Channel distribution (Analytics Nivel 1)
// ============================
const CHANNEL_META = {
  google_ads: { label: "Google Ads", color: "#DC2626" },
  meta_ads: { label: "Meta Ads", color: "#3B82F6" },
  organico: { label: "Orgánico", color: "#10B981" },
  directo: { label: "Directo", color: "#6B7280" },
  otros: { label: "Otros", color: "#F59E0B" },
};

function ChannelDistribution({ dist }) {
  const pieData = Object.entries(dist)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: CHANNEL_META[k]?.label || k, value: v, key: k }));
  const total = pieData.reduce((a, b) => a + b.value, 0);
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-5 mb-6" data-testid="channel-distribution">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-bold text-base">Origen del tráfico</h3>
          <p className="text-xs text-neutral-500">
            Basado en UTMs y referrer · Total atribuido: {total}
          </p>
        </div>
      </div>
      {total === 0 ? (
        <div className="text-sm text-neutral-400 py-8 text-center">
          Aún no hay datos de atribución. Se poblará cuando lleguen leads con UTMs desde campañas.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
          <div className="min-w-0" style={{ width: "100%", height: 220, minHeight: 220 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={45}>
                  {pieData.map((e, i) => (
                    <Cell key={i} fill={CHANNEL_META[e.key]?.color || "#999"} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend iconType="circle" />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2">
            {pieData.map((e) => {
              const pct = total > 0 ? Math.round((e.value / total) * 100) : 0;
              return (
                <div key={e.key} className="flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ background: CHANNEL_META[e.key]?.color || "#999" }}
                  />
                  <span className="text-sm font-semibold text-neutral-800 flex-1">{e.name}</span>
                  <span className="text-sm text-neutral-600">{e.value}</span>
                  <span className="text-xs text-neutral-400 w-10 text-right">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
